import { useState } from 'react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { GooglePlacesAutocomplete } from '../ui/google-places-autocomplete';
import { WheelTimePicker } from './WheelTimePicker';
import { toast } from 'sonner';
import { Pencil, Clock, Loader2 } from 'lucide-react';
import { formatTimeFromDate } from '../../utils/dateFormatting';

const isoToHHMM = (iso) => {
  if (!iso) return '';
  return formatTimeFromDate(iso, '24h');
};

/**
 * Edit an existing route stop. Notes/ETA/label/address are editable while the
 * stop is not yet completed. Arrival and departure (completed) times can be
 * corrected any time — including after completion.
 */
export const EditRouteStopDialog = ({ jobId, stop, api, onUpdated, country = 'AU' }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [estArrival, setEstArrival] = useState('');
  const [address, setAddress] = useState({ address: '', suburb: '', state: '', postcode: '' });
  const [arrivedTime, setArrivedTime] = useState('');
  const [completedTime, setCompletedTime] = useState('');
  const [workDescription, setWorkDescription] = useState('');

  const isCompleted = stop.status === 'completed';
  const hasArrived = stop.status === 'arrived' || isCompleted;

  const initFromStop = () => {
    setLabel(stop.label || '');
    setNotes(stop.notes || '');
    setEstArrival(stop.est_arrival || '');
    setAddress({
      address: stop.address || '',
      suburb: stop.suburb || '',
      state: stop.state || '',
      postcode: stop.postcode || '',
    });
    setArrivedTime(isoToHHMM(stop.arrived_at));
    setCompletedTime(isoToHHMM(stop.completed_at));
    setWorkDescription(stop.work_description || '');
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (v) initFromStop();
  };

  const handlePlaceSelect = (place) => {
    setAddress({
      address: place.street_address || place.formatted_address || '',
      suburb: place.suburb || '',
      state: place.state || '',
      postcode: place.postcode || '',
    });
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (!isCompleted) {
        payload.label = label.trim() || null;
        payload.notes = notes || null;
        payload.est_arrival = estArrival || null;
        payload.address = address.address;
        payload.suburb = address.suburb;
        payload.state = address.state;
        payload.postcode = address.postcode;
      }
      // Time corrections — allowed at any status (only send when changed).
      if (hasArrived && arrivedTime && arrivedTime !== isoToHHMM(stop.arrived_at)) {
        payload.arrived_time = arrivedTime;
      }
      if (isCompleted && completedTime && completedTime !== isoToHHMM(stop.completed_at)) {
        payload.completed_time = completedTime;
      }
      // Description — editable at any status (send when changed).
      if (workDescription !== (stop.work_description || '')) {
        payload.work_description = workDescription.trim() || null;
      }
      if (Object.keys(payload).length === 0) {
        setOpen(false);
        return;
      }
      const res = await api.patch(`/api/jobs/${jobId}/stops/${stop.id}`, payload);
      onUpdated(res.data.stops, res.data.route_summary);
      toast.success('Stop updated');
      setOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stop');
    } finally {
      setSaving(false);
    }
  };

  const currentAddress = [address.address, address.suburb, address.state, address.postcode].filter(Boolean).join(', ');

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="w-full mt-2 border-[var(--m-border-light)] text-[var(--m-text-body)] hover:bg-[var(--m-bg-elevated)]/30"
        onClick={() => handleOpenChange(true)}
        data-testid={`stop-edit-btn-${stop.id}`}
      >
        {isCompleted
          ? <><Clock className="h-3.5 w-3.5 mr-1" /> Edit times &amp; notes</>
          : <><Pencil className="h-3.5 w-3.5 mr-1" /> Edit stop</>}
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-stop-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--m-text)] capitalize">Edit {stop.type || 'stop'}</DialogTitle>
            <DialogDescription className="text-[var(--m-text-secondary)] text-xs">
              {isCompleted ? 'Correct the start / finish times and description for this stop.' : 'Update the location, ETA, notes or address for this stop.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {!isCompleted && (
              <>
                <div>
                  <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Location name / title</label>
                  <input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="e.g. North Depot (optional if address given)"
                    className="w-full h-10 px-3 rounded-md bg-[var(--m-bg)] border border-[var(--m-border-light)] text-[var(--m-text)] text-sm placeholder:text-[var(--m-text-muted)] focus:outline-none focus:ring-1 focus:ring-[#ffd108]"
                    data-testid="edit-stop-label"
                  />
                </div>

                <div>
                  <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Address</label>
                  {currentAddress && <p className="text-[var(--m-text-body)] text-xs mb-1">Current: {currentAddress}</p>}
                  <GooglePlacesAutocomplete
                    onPlaceSelect={handlePlaceSelect}
                    placeholder="Search a new address (optional)..."
                    country={country}
                    className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]"
                    data-testid="edit-stop-address"
                  />
                </div>

                <div>
                  <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">ETA (optional)</label>
                  <WheelTimePicker
                    value={estArrival}
                    onChange={setEstArrival}
                    minuteStep={5}
                    className="w-full h-10 bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[var(--m-bg)] hover:text-[var(--m-text)]"
                    testId="edit-stop-eta"
                  />
                </div>

                <div>
                  <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Notes</label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any details for this stop..."
                    className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] resize-none"
                    rows={3}
                    data-testid="edit-stop-notes"
                  />
                </div>
              </>
            )}

            {hasArrived && (
              <div>
                <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Start time</label>
                <WheelTimePicker
                  value={arrivedTime}
                  onChange={setArrivedTime}
                  minuteStep={5}
                  className="w-full h-10 bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[var(--m-bg)] hover:text-[var(--m-text)]"
                  testId="edit-stop-arrived-time"
                />
              </div>
            )}
            {isCompleted && (
              <div>
                <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Finish time</label>
                <WheelTimePicker
                  value={completedTime}
                  onChange={setCompletedTime}
                  minuteStep={5}
                  className="w-full h-10 bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[var(--m-bg)] hover:text-[var(--m-text)]"
                  testId="edit-stop-completed-time"
                />
              </div>
            )}
            {isCompleted && (
              <div>
                <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Description of works</label>
                <Textarea
                  value={workDescription}
                  onChange={(e) => setWorkDescription(e.target.value)}
                  placeholder="Any work notes for this stop..."
                  className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] resize-none"
                  rows={3}
                  data-testid="edit-stop-work-description"
                />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" className="border-[var(--m-border-light)] text-[var(--m-text-body)]" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button className="bg-[#ffd108] text-[#1a1a1a] hover:bg-[#ffd108]/90" onClick={handleSubmit} disabled={saving} data-testid="edit-stop-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditRouteStopDialog;
